import { Instagram, Facebook, Youtube, Mail, MapPin, Phone, MessageCircle, Star } from "lucide-react";
import { LOGO_URL, CENTERS } from "@/lib/content";

export default function Footer() {
  return (
    <footer className="bg-black border-t border-zinc-900" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Horsepower" className="h-12 w-12 object-contain" />
              <div className="leading-none">
                <div className="font-display text-2xl text-white">HORSEPOWER</div>
                <div className="font-mono text-[10px] text-[#FFD60A] tracking-[0.3em] mt-1">ACADEMY</div>
              </div>
            </div>
            <p className="mt-6 text-zinc-400 leading-relaxed max-w-md text-sm">
              Le réseau Horsepower forme les professionnels de la reprogrammation moteur depuis le terrain. E85, Stage 1, Stage 2, business : tout pour lancer une activité rentable.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <a href="https://www.instagram.com/horsepower_beziers/" target="_blank" rel="noreferrer" className="w-10 h-10 border border-zinc-800 hover:border-[#FFD60A] hover:bg-[#FFD60A]/10 flex items-center justify-center transition-all" data-testid="footer-social-instagram">
                <Instagram size={16} className="text-zinc-400" />
              </a>
              <a href="https://wa.me/33678229256" target="_blank" rel="noreferrer" className="w-10 h-10 border border-zinc-800 hover:border-[#FFD60A] hover:bg-[#FFD60A]/10 flex items-center justify-center transition-all" data-testid="footer-social-whatsapp" aria-label="WhatsApp">
                <MessageCircle size={16} className="text-zinc-400" />
              </a>
              <a href="#" className="w-10 h-10 border border-zinc-800 hover:border-[#FFD60A] hover:bg-[#FFD60A]/10 flex items-center justify-center transition-all" data-testid="footer-social-facebook">
                <Facebook size={16} className="text-zinc-400" />
              </a>
              <a href="https://www.youtube.com/@HorsePowerGarage" target="_blank" rel="noreferrer" className="w-10 h-10 border border-zinc-800 hover:border-[#FFD60A] hover:bg-[#FFD60A]/10 flex items-center justify-center transition-all" data-testid="footer-social-youtube" aria-label="YouTube Horsepower Garage">
                <Youtube size={16} className="text-zinc-400" />
              </a>
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FFD60A] mb-5">Nos centres Instagram</div>
            <ul className="space-y-2.5 text-sm">
              {CENTERS.map((c) => (
                <li key={c.city}>
                  <a
                    href={c.url}
                    target="_blank"
                    rel="noreferrer"
                    className="group flex items-center gap-2 text-zinc-400 hover:text-[#FFD60A] transition-colors"
                    data-testid={`footer-center-${c.city.toLowerCase()}`}
                  >
                    <Instagram size={13} className={c.primary ? "text-[#FFD60A]" : ""} />
                    <span className="font-mono text-xs">@{c.instagram}</span>
                    {c.primary && (
                      <span className="ml-1 inline-flex items-center gap-1 text-[9px] uppercase tracking-widest text-[#FFD60A]">
                        <Star size={8} fill="currentColor" /> Principal
                      </span>
                    )}
                  </a>
                </li>
              ))}
            </ul>

            <div className="mt-7 pt-6 border-t border-zinc-900">
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FFD60A] mb-3">File Service Pro</div>
              <a
                href="https://horsepowertuningfiles.com/"
                target="_blank"
                rel="noreferrer"
                className="group flex items-center justify-between gap-2 text-zinc-300 hover:text-[#FFD60A] transition-colors"
                data-testid="footer-file-service-link"
              >
                <span className="text-sm">horsepowertuningfiles.com</span>
                <span className="text-xs">→</span>
              </a>
              <div className="text-[10px] text-zinc-500 mt-1.5 font-mono uppercase tracking-widest">Fichiers reprog pro · 15 min</div>
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FFD60A] mb-5">Contact</div>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-3 text-zinc-400">
                <Mail size={16} className="text-[#FFD60A] mt-0.5 shrink-0" />
                <a href="mailto:direction@horsepower-garage.fr" className="hover:text-white transition-colors" data-testid="footer-email">direction@horsepower-garage.fr</a>
              </li>
              <li className="flex items-start gap-3 text-zinc-400">
                <Phone size={16} className="text-[#FFD60A] mt-0.5 shrink-0" />
                <a href="tel:+33671168024" className="hover:text-white transition-colors" data-testid="footer-phone">06 71 16 80 24</a>
              </li>
              <li className="flex items-start gap-3 text-zinc-400">
                <MessageCircle size={16} className="text-[#FFD60A] mt-0.5 shrink-0" />
                <a href="https://wa.me/33678229256" target="_blank" rel="noreferrer" className="hover:text-white transition-colors" data-testid="footer-whatsapp">WhatsApp · 06 78 22 92 56</a>
              </li>
              <li className="flex items-start gap-3 text-zinc-400">
                <MapPin size={16} className="text-[#FFD60A] mt-0.5 shrink-0" />
                <span>Horsepower Béziers — France</span>
              </li>
            </ul>
            <a href="#contact" className="mt-6 inline-flex px-5 py-2.5 border border-white text-white text-xs font-bold uppercase tracking-widest hover:bg-white hover:text-black transition-all" data-testid="footer-contact-cta">
              Nous écrire
            </a>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-zinc-900 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-zinc-600">
          <div className="font-mono uppercase tracking-widest">© 2026 Horsepower Academy. Tous droits réservés.</div>
          <div className="flex items-center gap-6">
            <a href="#" className="hover:text-white transition-colors">Mentions légales</a>
            <a href="#" className="hover:text-white transition-colors">CGV</a>
            <a href="#" className="hover:text-white transition-colors">Politique de confidentialité</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
